import { useCallback } from 'react'
import type { StudioActionDeps } from './proposalStudioActionTypes'

export function useProposalStudioItemListActions(deps: StudioActionDeps) {
  const {
    proposalItems,
    activeIndex,
    draggedIndex,
    setProposalItems,
    setActiveIndex,
    setDraggedIndex,
    setDragOverIndex,
  } = deps

  const handleRemoveItem = useCallback(
    (id: string) => {
      setProposalItems((prev) => prev.filter((i) => i.id !== id))
      if (activeIndex >= proposalItems.length - 1 && activeIndex > 0) {
        setActiveIndex(activeIndex - 1)
      }
    },
    [activeIndex, proposalItems.length, setProposalItems, setActiveIndex],
  )

  const handleUpdateQty = useCallback(
    (id: string, qty: number) => {
      if (qty < 1) return
      setProposalItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, quantidade: qty, total: qty * i.valorUnitario } : i)),
      )
    },
    [setProposalItems],
  )

  const handleUpdateValor = useCallback(
    (id: string, valor: number) => {
      if (valor < 0) return
      setProposalItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, valorUnitario: valor, total: i.quantidade * valor } : i)),
      )
    },
    [setProposalItems],
  )

  const handleUpdateDescricao = useCallback(
    (id: string, descricao: string) => {
      setProposalItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, descricao, nome: descricao, name: descricao } : i)),
      )
    },
    [setProposalItems],
  )

  const handleDragStartItem = useCallback(
    (index: number) => {
      setDraggedIndex(index)
    },
    [setDraggedIndex],
  )

  const handleDragOverItem = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault()
      setDragOverIndex(index)
    },
    [setDragOverIndex],
  )

  const handleDropItem = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault()
      if (draggedIndex === null || draggedIndex === dropIndex) {
        setDraggedIndex(null)
        setDragOverIndex(null)
        return
      }
      setProposalItems((prev) => {
        const items = [...prev]
        const [removed] = items.splice(draggedIndex, 1)
        items.splice(dropIndex, 0, removed)
        return items
      })
      setDraggedIndex(null)
      setDragOverIndex(null)
    },
    [draggedIndex, setProposalItems, setDraggedIndex, setDragOverIndex],
  )

  const handleDragEndItem = useCallback(() => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }, [setDraggedIndex, setDragOverIndex])

  return {
    handleRemoveItem,
    handleUpdateQty,
    handleUpdateValor,
    handleUpdateDescricao,
    handleDragStartItem,
    handleDragOverItem,
    handleDropItem,
    handleDragEndItem,
  }
}
